import { ErrorHandler } from '@/helpers/errorHandler';
import SentryClient from '@/helpers/sentry';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { MicrocertificateState } from './state';
import {
  EligibilityStatus,
  MicrocertificatesService,
} from '@/services/undergrad/pegasus-pepe-legal/microcertificates.service';
import i18n from '@/i18n';
import { UndergradModule } from '@/models/undergrad/undergrad-module.model';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { UserMicrocertificate } from './types';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getMicrocertificateStatus } from '@/helpers/microcertificate';

const SET_MICROCERTIFICATES = 'SET_MICROCERTIFICATES';
const SET_GENERATING_MICROCERTIFICATE = 'SET_GENERATING_MICROCERTIFICATE';
const SET_MICROCERTIFICATE_FILES = 'SET_MICROCERTIFICATE_FILES';
const SET_MICROCERTIFICATE_HASH = 'SET_MICROCERTIFICATE_HASH';

const actions: ActionTree<MicrocertificateState, RootState> = {
  async VERIFY_MICROCERTIFICATES_ELIGIBILITY({ getters, dispatch }): Promise<void> {
    try {
      const { verifyMicrocertificatesEligibilityAutomatically, currentUndergradCourse } = getters;

      if (verifyMicrocertificatesEligibilityAutomatically) {
        const microcertificatesService = new MicrocertificatesService();
        const eligibilitiesStatuses =
          await microcertificatesService.setMicrocertificatesEligibility(currentUndergradCourse.id);

        const isNewMicrocertificateCreated = eligibilitiesStatuses.some(({ status }) => {
          return status === EligibilityStatus.ELIGIBILITY_CREATED;
        });

        if (isNewMicrocertificateCreated) {
          // Reload the microcertificates in store
          dispatch('GET_USER_MICROCERTIFICATES', { noCache: true });
        }
      }
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async GET_USER_MICROCERTIFICATES(
    { getters, commit },
    options = { noCache: false },
  ): Promise<void> {
    try {
      const { undergradCycles: submodules, undergradDisciplines: disciplines } = getters;

      const submodulesIds: string[] = submodules.map((submodule: UndergradModule) => submodule.id);
      const complementaryDisciplinesIds: string[] = (disciplines ?? [])
        .filter(
          (discipline: UndergradDiscipline) =>
            discipline.submoduleType?.toUpperCase() === 'COMPLEMENTAR',
        )
        .map((discipline: UndergradDiscipline) => discipline.id);

      const microcertificatesService = new MicrocertificatesService();
      const userMicrocertificates = await microcertificatesService.getUserMicrocertificates(
        submodulesIds,
        complementaryDisciplinesIds,
        options.noCache,
      );

      commit(SET_MICROCERTIFICATES, userMicrocertificates);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async GENERATE_MICROCERTIFICATE({ commit }, microcertificateId): Promise<void> {
    commit(SET_GENERATING_MICROCERTIFICATE, true);

    try {
      const microcertificatesService = new MicrocertificatesService();
      const { pdfUrl, pngUrl, hash } = await microcertificatesService.getMicrocertificateFiles(
        microcertificateId,
      );

      commit(SET_MICROCERTIFICATE_FILES, { pdfUrl, pngUrl });
      commit(SET_MICROCERTIFICATE_HASH, hash);
    } catch (error: unknown) {
      ErrorHandler.showErrorMessage({
        customMessage: i18n.t('microcertificateGenerationError').toString(),
        error,
      });
    } finally {
      commit(SET_GENERATING_MICROCERTIFICATE, false);
    }
  },
  async OPEN_MICROCERTIFICATE_MODAL(
    { commit, rootGetters, state },
    args: {
      userMicrocertificate: UserMicrocertificate;
      page?: string;
      origin?: string;
      component?: string;
    },
  ): Promise<void> {
    // **Origin** identifies if the action was acted by the user, automatically or from URL. Possible values are:
    //      'auto-popup', 'user-click', 'external-link'
    // **Components** maps the visual components that user interacted to open the modal. Possible values are:
    //      'notification', 'certificate-card', 'discipline-card-button', 'discipline-card-stamp',
    //      'microcertificate-card', 'microcertificate-card-button', 'activities-card', 'onboarding-complementary-discs'

    const { userMicrocertificate, page, origin, component } = args;
    if (origin === 'auto-popup' && !state.acceptsMicrocertificateAutoPopup) return;

    commit('PUSH_MICROCERTIFICATE_TO_DISPLAY', userMicrocertificate.microcertificateId);

    const microcertificateType = userMicrocertificate.disciplineId ? 'discipline' : 'submodule';

    Analytics.trackEvent(EVENT_LABELS.microcertificateInteracted, {
      interaction_type: 'Microcertificate Opened',
      course: rootGetters.currentUndergradCourse?.name || '',
      microcertificate_name: userMicrocertificate.microcertificateName,
      category: 'Faculdade',
      microcertificate_type: microcertificateType,
      microcertificate_level: Number.isFinite(userMicrocertificate.progress)
        ? getMicrocertificateStatus(userMicrocertificate.progress).discipline
        : null,
      page: page || null,
      origin: origin || null,
      component: component || null,
    });
  },
};

export default actions;
